// Pure grid logic (sorting, filtering, quick filter, CSV) — unit-tested without React.
import type { GridColDef, GridColType, GridFilterItem, GridFilterModel, GridSortModel } from "./types";

export const OPERATORS_BY_TYPE: Record<GridColType, string[]> = {
  string: ["contains", "equals", "startsWith", "endsWith", "isEmpty", "isNotEmpty"],
  number: ["=", "!=", ">", ">=", "<", "<=", "isEmpty", "isNotEmpty"],
  date: ["is", "not", "after", "onOrAfter", "before", "onOrBefore", "isEmpty", "isNotEmpty"],
  dateTime: ["after", "onOrAfter", "before", "onOrBefore", "isEmpty", "isNotEmpty"],
  boolean: ["is"],
  singleSelect: ["is", "not", "isAnyOf"],
  actions: [],
};

/** Operators that need no value. */
export const VALUELESS = new Set(["isEmpty", "isNotEmpty"]);

export function colType<R>(c: GridColDef<R>): GridColType {
  return c.type ?? "string";
}

export function operatorsFor<R>(c: GridColDef<R>): string[] {
  const all = OPERATORS_BY_TYPE[colType(c)];
  return c.filterOperators ? all.filter((o) => c.filterOperators!.includes(o)) : all;
}

export function cellValue<R>(c: GridColDef<R>, row: R): unknown {
  return c.valueGetter ? c.valueGetter(row) : (row as Record<string, unknown>)[c.field];
}

const isBlank = (v: unknown) => v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length);

export function formatValue<R>(c: GridColDef<R>, row: R, value = cellValue(c, row)): string {
  if (c.valueFormatter) return c.valueFormatter(value, row);
  if (isBlank(value)) return "";
  if (c.type === "singleSelect") {
    const values = Array.isArray(value) ? value : [value];
    return values
      .map((v) => c.valueOptions?.find((o) => o.value === String(v))?.label ?? String(v))
      .join(", ");
  }
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function toTime(v: unknown): number | null {
  if (isBlank(v)) return null;
  const t = v instanceof Date ? v.getTime() : new Date(String(v)).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Day (YYYY-MM-DD) comparison for `date` columns; exact instants for `dateTime`. */
function timeKey(v: unknown, type: GridColType): number | null {
  const t = toTime(v);
  if (t === null) return null;
  if (type !== "date") return t;
  const d = new Date(t);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function compareValues(a: unknown, b: unknown, type: GridColType): number {
  const ba = isBlank(a);
  const bb = isBlank(b);
  if (ba || bb) return ba && bb ? 0 : ba ? 1 : -1; // blanks last (before direction flip handled by caller)
  if (type === "number") return Number(a) - Number(b);
  if (type === "date" || type === "dateTime") return (toTime(a) ?? 0) - (toTime(b) ?? 0);
  if (type === "boolean") return Number(Boolean(a)) - Number(Boolean(b));
  return String(Array.isArray(a) ? a.join(",") : a).localeCompare(
    String(Array.isArray(b) ? b.join(",") : b),
    undefined,
    { numeric: true, sensitivity: "base" },
  );
}

export function sortRows<R>(rows: R[], model: GridSortModel, columns: GridColDef<R>[]): R[] {
  const active = model
    .map((s) => ({ s, c: columns.find((c) => c.field === s.field) }))
    .filter((x): x is { s: (typeof model)[number]; c: GridColDef<R> } => Boolean(x.c));
  if (!active.length) return rows;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((x, y) => {
      for (const { s, c } of active) {
        const a = cellValue(c, x.row);
        const b = cellValue(c, y.row);
        // blanks stay last in both directions
        if (isBlank(a) !== isBlank(b)) return isBlank(a) ? 1 : -1;
        const r = c.sortComparator ? c.sortComparator(a, b) : compareValues(a, b, colType(c));
        if (r !== 0) return s.sort === "asc" ? r : -r;
      }
      return x.i - y.i; // stable
    })
    .map((x) => x.row);
}

export function matchesItem<R>(c: GridColDef<R>, row: R, item: GridFilterItem): boolean {
  const v = cellValue(c, row);
  const op = item.operator;
  if (op === "isEmpty") return isBlank(v);
  if (op === "isNotEmpty") return !isBlank(v);
  const want = item.value;
  if (want === undefined || want === "" || (Array.isArray(want) && !want.length)) return true; // incomplete
  const type = colType(c);
  if (type === "number") {
    const a = Number(v);
    const b = Number(want);
    if (isBlank(v) || Number.isNaN(b)) return false;
    return op === "="
      ? a === b
      : op === "!="
        ? a !== b
        : op === ">"
          ? a > b
          : op === ">="
            ? a >= b
            : op === "<"
              ? a < b
              : a <= b;
  }
  if (type === "date" || type === "dateTime") {
    const a = timeKey(v, type);
    const b = timeKey(want, type);
    if (a === null || b === null) return false;
    return op === "is"
      ? a === b
      : op === "not"
        ? a !== b
        : op === "after"
          ? a > b
          : op === "onOrAfter"
            ? a >= b
            : op === "before"
              ? a < b
              : a <= b;
  }
  if (type === "boolean") return Boolean(v) === (want === true || want === "true");
  if (type === "singleSelect") {
    const values = (Array.isArray(v) ? v : [v]).map(String);
    if (op === "isAnyOf")
      return (Array.isArray(want) ? want : [want]).some((w) => values.includes(String(w)));
    return op === "is" ? values.includes(String(want)) : !values.includes(String(want));
  }
  const text = formatValue(c, row, v).toLowerCase();
  const w = String(want).toLowerCase();
  return op === "equals"
    ? text === w
    : op === "startsWith"
      ? text.startsWith(w)
      : op === "endsWith"
        ? text.endsWith(w)
        : text.includes(w);
}

/** Filter items that can take effect (column known, value present when needed). */
export function effectiveItems<R>(model: GridFilterModel, columns: GridColDef<R>[]): GridFilterItem[] {
  return model.items.filter((i) => {
    if (!columns.some((c) => c.field === i.field)) return false;
    if (VALUELESS.has(i.operator)) return true;
    const v = i.value;
    return v !== undefined && v !== "" && !(Array.isArray(v) && !v.length);
  });
}

export function filterRows<R>(rows: R[], model: GridFilterModel, columns: GridColDef<R>[]): R[] {
  const items = effectiveItems(model, columns);
  const tokens = model.quickFilterValues.map((t) => t.toLowerCase()).filter(Boolean);
  if (!items.length && !tokens.length) return rows;
  const searchable = columns.filter((c) => c.type !== "actions");
  return rows.filter((row) => {
    if (items.length) {
      const results = items.map((i) =>
        matchesItem(
          columns.find((c) => c.field === i.field)!,
          row,
          i,
        ),
      );
      const ok = model.logicOperator === "or" ? results.some(Boolean) : results.every(Boolean);
      if (!ok) return false;
    }
    // Quick filter: every token must appear in some column (MUI default).
    return tokens.every((t) => searchable.some((c) => formatValue(c, row).toLowerCase().includes(t)));
  });
}

const csvCell = (s: string) => {
  // Spreadsheet formula injection guard (=, +, -, @ at the start).
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export function rowsToCsv<R>(rows: R[], columns: GridColDef<R>[]): string {
  const cols = columns.filter((c) => !c.disableExport && c.type !== "actions");
  const lines = [cols.map((c) => csvCell(c.headerName)).join(",")];
  for (const row of rows) lines.push(cols.map((c) => csvCell(formatValue(c, row))).join(","));
  return "﻿" + lines.join("\r\n"); // BOM: Excel opens Thai text correctly
}
